import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, ArrowLeft, AlertCircle, X } from 'lucide-react'
import axios from '../../lib/axios'

// Timezone offset map (minutes from UTC, positive = east of UTC)
const TIMEZONE_OFFSETS = {
  UTC: 0, GMT: 0, EST: -300, EDT: -240, CST: -360, CDT: -300,
  MST: -420, MDT: -360, PST: -480, PDT: -420,
  IST: 330, CET: 60, CEST: 120, JST: 540, AEST: 600, AEDT: 660,
}

// Convert a local time string "HH:MM" in the given timezone to UTC "HH:MM"
const convertToUTC = (timeStr, timezone) => {
  if (!timeStr) return null
  const offset = TIMEZONE_OFFSETS[timezone] || 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  date.setMinutes(date.getMinutes() - offset)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}
import WebsiteScrapeInput from './WebsiteScrapeInput'
import ScrapedDataConfirmation from './ScrapedDataConfirmation'
import LeadSelectionStep from './LeadSelectionStep'
import LeadProfileInput from './LeadProfileInput'
import AIGeneratedPreview from './AIGeneratedPreview'
import './AICreateCampaign.css'
import '../Pages.css'

const WIZARD_STEPS = [
  { num: 1, label: 'Analyze' },
  { num: 2, label: 'Confirm' },
  { num: 3, label: 'Add Leads' },
  { num: 4, label: 'Profile' },
  { num: 5, label: 'Generate' }
]

const DEFAULT_LEAD_PROFILE = {
  persona: '',
  leadIndustry: '',
  senderRole: '',
  companySize: '',
  timezone: '',
  tone: 'professional',
  emailStyle: 'concise',
  customPrompt: ''
}

export default function AICreateCampaign() {
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(0)
  const [scraping, setScraping] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [websiteUrl, setWebsiteUrl] = useState('')
  const [scrapedData, setScrapedData] = useState(null)
  const [selectedLeads, setSelectedLeads] = useState(null)
  const [availableVariables, setAvailableVariables] = useState([])
  const [leadProfile, setLeadProfile] = useState(DEFAULT_LEAD_PROFILE)
  const [aiCampaign, setAiCampaign] = useState(null)

  // Animated step counters for progress spinners
  const [scrapeStep, setScrapeStep] = useState(0)
  const [generateStep, setGenerateStep] = useState(0)

  // ── Step 0: Scrape website ─────────────────────────────────────
  const handleScrape = async () => {
    setError(null)
    setScraping(true)
    setScrapeStep(0)

    const stepInterval = setInterval(() => {
      setScrapeStep(prev => (prev < 3 ? prev + 1 : prev))
    }, 900)

    try {
      const res = await axios.post('ai/scrape-website', { url: websiteUrl })
      clearInterval(stepInterval)
      setScrapeStep(4)
      setScrapedData(res.data)
      // Pre-fill lead timezone from scraped data if available
      if (res.data?.detectedTimezone) {
        setLeadProfile(prev => ({ ...prev, timezone: prev.timezone || res.data.detectedTimezone }))
      }
      setCurrentStep(1)
    } catch (err) {
      clearInterval(stepInterval)
      setError(err.response?.data?.detail || 'Failed to analyse website. Please check the URL and try again.')
    } finally {
      setScraping(false)
    }
  }

  // ── Step 3→4: Generate campaign using company + lead data + variables ──
  const handleGenerate = async () => {
    setError(null)
    setGenerating(true)
    setGenerateStep(0)

    const stepInterval = setInterval(() => {
      setGenerateStep(prev => (prev < 3 ? prev + 1 : prev))
    }, 1200)

    try {
      const res = await axios.post('ai/generate-campaign', {
        scrapedData,
        leadProfile,   // ← persona, industry, companySize, timezone, tone, customPrompt
        availableVariables: availableVariables.map(v => ({ key: v.key, label: v.label, custom: !!v.custom })),
        leadCount: selectedLeads?.count || 0
      })
      clearInterval(stepInterval)
      setGenerateStep(4)

      // Override schedule timezone from leadProfile if specified
      const campaign = res.data
      if (leadProfile.timezone && campaign?.schedule) {
        campaign.schedule.timezone = leadProfile.timezone
        // Set sensible from/to time defaults based on timezone
        campaign.schedule.fromTime = campaign.schedule.fromTime || '09:00'
        campaign.schedule.toTime = campaign.schedule.toTime || '17:00'
        campaign.schedule.days = campaign.schedule.days?.length
          ? campaign.schedule.days
          : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
      }

      setAiCampaign(campaign)
      setCurrentStep(4)
    } catch (err) {
      clearInterval(stepInterval)
      const errData = err.response?.data
      if (errData?.error === 'LIMIT_EXCEEDED' && errData?.resource === 'ai_credits') {
        // Handled by the global AICreditsModal (see src/lib/axios.js interceptor).
      } else if (errData?.error === 'LIMIT_EXCEEDED') {
        setError(`You've used ${errData.used} of ${errData.limit} AI email credits. Please upgrade your plan to continue.`)
      } else {
        setError(errData?.detail || 'Failed to generate campaign. Please try again.')
      }
    } finally {
      setGenerating(false)
    }
  }

  // ── Step 3: Save all 4 endpoints ──────────────────────────────
  const handleCreateCampaign = async () => {
    setError(null)
    setSaving(true)

    try {
      // 1. Create campaign record
      const createRes = await axios.post('email-campaign/create', {
        sender_ids: [0],
        email_body: 'Replace Content Here',
        subject: 'Replace Subject line here...',
        campaign_cron_time: new Date().toISOString(),
        master_template_id: 0,
        name: aiCampaign.name
      })
      const campaignId = createRes.data.id

      // 2. Save schedule — convert local times to UTC before storing
      const { schedule } = aiCampaign
      const schedTZ = schedule.timezone || leadProfile.timezone || 'UTC'
      await axios.post(`email-campaign/schedule/${campaignId}`, {
        campaign_id: campaignId,
        schedule_name: schedule.scheduleName || aiCampaign.name + ' Schedule',
        start_date: schedule.startDate || null,
        end_date: schedule.endDate || null,
        from_time: convertToUTC(schedule.fromTime || '09:00', schedTZ),
        to_time:   convertToUTC(schedule.toTime   || '17:00', schedTZ),
        timezone: schedTZ,
        days: schedule.days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
      })

      // 3. Save sequence — use exact field names the backend expects
      const { steps } = aiCampaign.sequence
      await axios.post(`email-campaign/sequence/${campaignId}`, {
        campaignId,
        steps: steps.map(step => ({
          delay: step.delay || 0,
          variants: step.variants.map(v => ({
            id: v.id || 'A',
            templateId: v.templateId ? String(v.templateId) : '',
            subject: v.subject || '',
            body: v.body || '',
            split: v.split ?? 100
          }))
        }))
      })

      // 4. Save options
      const { options } = aiCampaign
      await axios.post(`email-campaign/options/${campaignId}`, {
        id: campaignId,
        accountIds: options.accountIds || [],
        stopOnReply: options.stopOnReply,
        openTracking: options.openTracking,
        linkTracking: options.linkTracking,
        deliveryOptimization: options.deliveryOptimization,
        sendTextOnly: options.sendTextOnly,
        sendFirstEmailTextOnly: options.sendFirstEmailTextOnly,
        emailsPerDay: options.emailsPerDay,
        delaySeconds: options.delaySeconds
      })

      // 5. Import leads into campaign if CSV was uploaded
      if (selectedLeads?.source === 'csv' && selectedLeads.rows?.length > 0) {
        try {
          await axios.post(`contacts/create-bulk/${campaignId}`, {
            rows: selectedLeads.rows,
            mapping: selectedLeads.columns.map(({ name, selectedType }) => ({
              column: name,
              mappedTo: selectedType,
            })),
          })
        } catch {
          // Non-blocking — leads can be added later
        }
      }

      navigate(`/campaigns/analytics/${campaignId}`)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create campaign. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page animate-fade-in">

      {/* Page Header */}
      <div className="page-header">
        <div className="header-info">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={22} style={{ color: '#8b5cf6' }} />
            AI Campaign Creator
          </h2>
          <p>Let AI build a personalised campaign from a website URL + your lead profile</p>
        </div>
        <button onClick={() => navigate('/campaigns')} className="btn btn-ghost">
          <ArrowLeft size={16} />
          Back to Campaigns
        </button>
      </div>

      {/* 4-Step Progress Bar */}
      <div className="campaign-steps" style={{ marginBottom: '1.75rem' }}>
        {WIZARD_STEPS.map((step, i) => (
          <div key={step.num} style={{ display: 'flex', alignItems: 'center' }}>
            <div
              className={`campaign-step${i === currentStep ? ' active' : ''}${i < currentStep ? ' done' : ''}`}
              style={i < currentStep ? { opacity: 0.7 } : {}}
            >
              <span className="campaign-step-number">{step.num}</span>
              <span>{step.label}</span>
            </div>
            {i < WIZARD_STEPS.length - 1 && <div className="campaign-step-connector" />}
          </div>
        ))}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="ai-error-banner" style={{ marginBottom: '1.25rem' }}>
          <AlertCircle size={18} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Step 0 — URL Input */}
      {currentStep === 0 && (
        <WebsiteScrapeInput
          url={websiteUrl}
          onUrlChange={setWebsiteUrl}
          onScrape={handleScrape}
          scraping={scraping}
          activeStep={scrapeStep}
        />
      )}

      {/* Step 1 — Review Scraped Data */}
      {currentStep === 1 && scrapedData && (
        <ScrapedDataConfirmation
          data={scrapedData}
          onChange={setScrapedData}
          onBack={() => setCurrentStep(0)}
          onNext={() => setCurrentStep(2)}
        />
      )}

      {/* Step 2 — Add Leads (new) */}
      {currentStep === 2 && (
        <LeadSelectionStep
          onBack={() => setCurrentStep(1)}
          onNext={() => setCurrentStep(3)}
          selectedLeads={selectedLeads}
          onLeadsChange={setSelectedLeads}
          availableVariables={availableVariables}
          onVariablesChange={setAvailableVariables}
        />
      )}

      {/* Step 3 — Lead Profile */}
      {currentStep === 3 && (
        <LeadProfileInput
          profile={leadProfile}
          onChange={setLeadProfile}
          onBack={() => setCurrentStep(2)}
          onGenerate={handleGenerate}
          generating={generating}
          generateStep={generateStep}
          availableVariables={availableVariables}
        />
      )}

      {/* Step 4 — Editable AI Preview */}
      {currentStep === 4 && aiCampaign && (
        <AIGeneratedPreview
          campaign={aiCampaign}
          onChange={setAiCampaign}
          onBack={() => setCurrentStep(3)}
          onCreate={handleCreateCampaign}
          saving={saving}
        />
      )}

    </div>
  )
}
